import { createAsyncSelectors } from '@lib/selectors';
import { HttpStatusCode } from '@sonar-software/sonar-http';
import { setJWTToken } from '@sonar-software/sonar-ts-lib';
import { useAction } from './useAction';
import { usePrevious } from './usePrevious';
import { useSonarSelector } from './useSonarSelector';
import { coreActions, PostSalesforceAuthLinkResponse } from '@sonar-software/sonar-core';
import { useCallback, useEffect, useRef } from 'react';

type ResponseHandler =
	(result: PostSalesforceAuthLinkResponse | null | undefined, statusCode: HttpStatusCode | null| undefined) => void;

export interface SalesforceLinkOptions {
	code: string;
	organizationId?: number;
	onResponse?: ResponseHandler;
}

const linkSelectors = createAsyncSelectors((state) => state.core.organizations.link.post);

export function useSalesforceLink(): (options: SalesforceLinkOptions) => void {
	const response = useSonarSelector(linkSelectors.data);
	const hasError = useSonarSelector(linkSelectors.isErrored);
	const hasFailure = useSonarSelector(linkSelectors.isFailed);
	const hasSuccess = useSonarSelector(linkSelectors.isReceived);
	const status = useSonarSelector(linkSelectors.statusCode);

	const previousHasError = usePrevious(hasError);
	const previousHasFailure = usePrevious(hasFailure);
	const previousHasSuccess = usePrevious(hasSuccess);

	const onResponseRef = useRef<ResponseHandler | null>(null);

	const linkSalesforce = useAction(coreActions.requestPostCoreOrganizationsLink);

	const hasNewError = hasError && !previousHasError;
	const hasNewFailure = hasFailure && !previousHasFailure;
	const hasNewSuccess = hasSuccess && !previousHasSuccess;

	const shouldTriggerResponse = hasNewError || hasNewFailure || hasNewSuccess;

	useEffect(() => {
		if (onResponseRef.current && shouldTriggerResponse) {
			onResponseRef.current(response, status);
		}
	}, [ response, shouldTriggerResponse, status ]);

	useEffect(() => {
		if (response?.updatedUserAccessToken) {
			setJWTToken(response.updatedUserAccessToken);
		}
	}, [ response?.updatedUserAccessToken ]);

	const link = useCallback(
		(options: SalesforceLinkOptions) => {
			const { code, organizationId, onResponse } = options;

			onResponseRef.current = onResponse ?? null;

			linkSalesforce({
				code,
				organizationId
			});
		},
		[ linkSalesforce ]
	);

	return link;
}
